import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectRosterDrafts,
  selectRosters,
  selectTargets,
  setDeptMetrics,
  setRosterDrafts,
  setRosters,
  setSaveError,
  setTargets,
} from '../../../../store/slices/peopleConfigSlice';
import { metricsToRedux } from '../../../../store/thunks/peopleConfigThunks';
import AdminButton from '../../../Admin/components/AdminButton';
import AdminSelect from '../../../Admin/components/AdminSelect';
import AdminTag from '../../../Admin/components/AdminTag';
import { AdminFormField } from '../../../Admin/components/AdminFormField';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigCard from '../components/ConfigCard';
import ConfigGhostButton from '../components/ConfigGhostButton';
import ConfigTable from '../components/ConfigTable';
import OrgDeptPicker from '../components/OrgDeptPicker';
import TabBody from '../components/TabBody';
import { usePeopleConfig } from '../PeopleConfigContext';
import { CONFIG_METRICS } from '../constants';
import {
  ConfigHistoryEntry,
  ConfigMetric,
  PendingTarget,
  TargetsSavePayload,
} from '../configTypes';
import {
  formatMetricValue,
  getApiErrorMessage,
  getNextFiscalWeek,
  getOwnTarget,
  getPendingTarget,
  getScopeLocations,
  locationKey,
  nationalKey,
  todayIso,
} from '../configUtils';
import MetricSetDrawer from './MetricSetDrawer';
import MetricTargetsDrawer from './MetricTargetsDrawer';
import {
  saveMetricSelection,
  saveMetricTargets,
  saveMobileRoster,
} from '../../../../api/peopleConfigApi';

type DrawerState = { kind: 'set' } | { kind: 'national' } | { kind: 'location'; loc?: string } | null;

const metricById = (id: string): ConfigMetric | undefined => CONFIG_METRICS.find((m) => m.id === id);

/**
 * Metrics used by each org and department, their effective dated national targets,
 * location overrides, and the mobile roster card.
 * A location override wins for an employee's department, otherwise the national target applies.
 */
const MetricsTab: React.FC = () => {
  const ctx = usePeopleConfig();
  const { org, dept, locations, status, deptMetrics, user } = ctx;
  const period = useSelector((state: any) => state.appData?.periodWeek?.data);
  const dispatch = useDispatch();
  const targets = useSelector(selectTargets);
  const rosters = useSelector(selectRosters);
  const rosterDrafts = useSelector(selectRosterDrafts);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [savingRoster, setSavingRoster] = useState(false);

  const today = todayIso();
  const next = getNextFiscalWeek(period);
  const key = `${org}:${dept}`;
  const nationalId = nationalKey(org, dept);
  const scopeLocations = getScopeLocations(locations, org, dept);
  const metrics = (deptMetrics[key] ?? []).map(metricById).filter((m): m is ConfigMetric => !!m);

  // Mobile roster card: three of the department's metrics, or all of them when it uses fewer than three.
  const slots = Math.min(3, metrics.length);
  const inDept = (ids: string[]) => ids.filter((id) => metrics.some((m) => m.id === id)).slice(0, slots);
  const roster = inDept(rosters[key] ?? metrics.slice(0, 3).map((m) => m.id));
  const rosterDraft = inDept(rosterDrafts[key] ?? roster);
  const rosterDup = new Set(rosterDraft).size !== rosterDraft.length;
  const rosterDirty = JSON.stringify(rosterDraft) !== JSON.stringify(roster);
  const rosterFull = rosterDraft.length === slots;
  const setRosterDraft = (v: string[]) => dispatch(setRosterDrafts({ ...rosterDrafts, [key]: v }));
  const names = (ids: string[]) => ids.map((id) => metricById(id)?.name ?? id).join(', ');

  const saveRoster = () =>
    ctx.guard(async () => {
      setSavingRoster(true);
      try {
        const updated = await saveMobileRoster({
          org,
          department: dept,
          metrics: rosterDraft,
          updatedBy: user,
        });
        const saved = metricsToRedux(updated)!;
        dispatch(setRosters({ ...rosters, [saved.key]: saved.roster }));
        dispatch(setRosterDrafts({ ...rosterDrafts, [saved.key]: saved.roster }));
        ctx.log([
          {
            section: 'Metrics',
            setting: 'Mobile roster card metrics',
            scope: `${org} ${dept}`,
            prior: names(roster),
            next: names(saved.roster),
            effective: 'Immediately',
          },
        ]);
      } catch (err) {
        console.error('[MetricsTab] Failed to save mobile roster:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
      } finally {
        setSavingRoster(false);
      }
    });

  const saveMetricSet = (ids: string[]) =>
    ctx.guard(async () => {
      try {
        const updated = await saveMetricSelection({
          org,
          department: dept,
          metrics: ids,
          updatedBy: user,
        });
        const saved = metricsToRedux(updated)!;
        ctx.log([
          {
            section: 'Metrics',
            setting: 'Metrics used',
            scope: `${org} ${dept}`,
            prior: names(deptMetrics[key] ?? []) || 'None',
            next: names(saved.usedMetrics),
            effective: 'Immediately',
          },
        ]);
        // The backend also trims the roster to the new metric set, so take both from the response.
        dispatch(setDeptMetrics({ ...deptMetrics, [saved.key]: saved.usedMetrics }));
        dispatch(setRosters({ ...rosters, [saved.key]: saved.roster }));
        dispatch(setRosterDrafts({ ...rosterDrafts, [saved.key]: saved.roster }));
        setDrawer(null);
      } catch (err) {
        console.error('[MetricsTab] Failed to save metric selection:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
      }
    });

  const saveTargets = ({ id, scope, nationalId: natId, form, initial, metrics: saved }: TargetsSavePayload) =>
    ctx.guard(async () => {
      const isLocation = id.startsWith('location:');
      const entries: Omit<ConfigHistoryEntry, 'at' | 'by'>[] = [];
      const apiTargets: Record<string, number> = {};

      saved.forEach((m) => {
        if (form[m.id] === initial[m.id]) return;
        const value = form[m.id] === '' ? null : Number(form[m.id]);
        const own = getOwnTarget(targets, id, m.id, today);
        const nat = isLocation ? getOwnTarget(targets, natId, m.id, today) : null;
        const prior =
          own !== null
            ? formatMetricValue(m, own)
            : isLocation && nat !== null
              ? `National ${formatMetricValue(m, nat)}`
              : 'Not set';

        entries.push({
          section: 'Metric targets',
          setting: `${m.name} target`,
          scope,
          prior,
          next: value === null ? (isLocation ? 'National target' : 'Not set') : formatMetricValue(m, value),
          effective: next.display,
        });

        // Only send explicitly changed metrics that have a value; null means "use national".
        // For location overrides, omit the metric to let the backend inherit from national.
        if (value !== null) {
          apiTargets[m.id] = value;
        }
      });

      if (entries.length === 0) {
        setDrawer(null);
        return;
      }

      try {
        const updated = await saveMetricTargets({
          org,
          department: dept,
          location: isLocation ? id.split(':')[1] : null,
          targets: apiTargets,
          updatedBy: user,
        });
        // The response holds the whole scope's targets (national + every override), the same
        // shape the page load stores, so it replaces `targets` rather than merging over stale ones.
        dispatch(setTargets(metricsToRedux(updated)!.targets));
        ctx.log(entries);
        setDrawer(null);
      } catch (err) {
        console.error('[MetricsTab] Failed to save metric targets:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
        // Do not close the drawer or log history — leave the user's edits in place.
      }
    });

  const pendingLine = (p: PendingTarget | null, m: ConfigMetric, inherit: string) =>
    p ? (
      <Text style={styles.pending}>
        {`${p.label || p.from}: ${p.value === null ? inherit : formatMetricValue(m, p.value)}`}
      </Text>
    ) : null;

  const overrideRows = scopeLocations.filter((l) =>
    metrics.some(
      (m) =>
        getOwnTarget(targets, locationKey(l.name, dept), m.id, today) !== null ||
        getPendingTarget(targets, locationKey(l.name, dept), m.id, today)
    )
  );

  return (
    <TabBody>
      <OrgDeptPicker />

      <ConfigCard
        title="Metrics and National Targets"
        subtitle={`The metrics ${org} ${dept} is measured on, and the primary target for each at every location.`}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <View style={styles.actions}>
            <AdminTag label="Effective dated" variant="blue" />
            <AdminButton
              label="Choose Metrics"
              variant="secondary"
              small
              disabled={status === 'loading'}
              onPress={() => setDrawer({ kind: 'set' })}
            />
            <AdminButton
              label="Edit Targets"
              small
              disabled={status === 'loading' || metrics.length === 0}
              onPress={() => setDrawer({ kind: 'national' })}
            />
          </View>
        }
      >
        <ConfigTable
          columns={[
            { key: 'm', header: 'Metric' },
            { key: 'd', header: 'Direction' },
            { key: 't', header: 'National target', align: 'right' },
          ]}
          rows={metrics.map((m) => {
            const v = getOwnTarget(targets, nationalId, m.id, today);
            return {
              key: m.id,
              cells: [
                <View key="m">
                  <Text style={styles.strong}>{m.name}</Text>
                  {m.unit ? <Text style={styles.sub}>{m.unit}</Text> : null}
                </View>,
                m.direction === 'lower' ? 'Lower is better' : 'Higher is better',
                <View key="t">
                  {v !== null ? (
                    <Text style={styles.own}>{formatMetricValue(m, v)}</Text>
                  ) : (
                    <Text style={styles.muted}>Not set</Text>
                  )}
                  {pendingLine(getPendingTarget(targets, nationalId, m.id, today), m, 'Not set')}
                </View>,
              ],
            };
          })}
        />
      </ConfigCard>

      <ConfigCard
        title="Location Overrides"
        subtitle={`Optional. A location value replaces the national target for ${dept} at that location. Grey values come from the national target.`}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <AdminButton
            label="Add Override"
            variant="secondary"
            small
            disabled={status === 'loading' || scopeLocations.length === 0 || metrics.length === 0}
            onPress={() => setDrawer({ kind: 'location' })}
          />
        }
      >
        <ConfigTable
          columns={[
            { key: 'loc', header: 'Location' },
            // Mockup: metric columns are `.num` (right aligned).
            ...metrics.map((m) => ({
              key: m.id,
              header: m.unit ? `${m.name} (${m.unit})` : m.name,
              align: 'right' as const,
            })),
            { key: 'act', header: 'Actions', hideHeader: true, flex: 0.5 },
          ]}
          emptyMessage={
            scopeLocations.length
              ? 'No location overrides. Every location uses the national targets.'
              : `No ${org} locations have ${dept} yet.`
          }
          rows={overrideRows.map((l) => {
            const id = locationKey(l.name, dept);
            return {
              key: l.name,
              cells: [
                <Text key="l" style={styles.strong}>{l.name}</Text>,
                ...metrics.map((m) => {
                  const own = getOwnTarget(targets, id, m.id, today);
                  const nat = getOwnTarget(targets, nationalId, m.id, today);
                  return (
                    <View key={m.id}>
                      {own !== null ? (
                        <Text style={styles.own}>{formatMetricValue(m, own)}</Text>
                      ) : (
                        <>
                          <Text style={styles.muted}>{formatMetricValue(m, nat)}</Text>
                          <Text style={styles.inheritTag}>National</Text>
                        </>
                      )}
                      {pendingLine(getPendingTarget(targets, id, m.id, today), m, 'National target')}
                    </View>
                  );
                }),
                <ConfigGhostButton
                  key="e"
                  label="Edit"
                  onPress={() => setDrawer({ kind: 'location', loc: l.name })}
                />,
              ],
            };
          })}
        />
      </ConfigCard>

      <ConfigCard
        title="Mobile Roster Card"
        subtitle={`The metrics shown on each ${dept} employee card in the My Team roster on mobile.`}
        status={status}
        onRetry={ctx.retry}
      >
        <View style={styles.rosterBody}>
          <View style={styles.rosterRow}>
            {['First metric', 'Second metric', 'Third metric'].slice(0, slots).map((label, i) => (
              <View key={`${key}-${label}`} style={styles.rosterCol}>
                <AdminFormField label={label}>
                  <AdminSelect
                    value={rosterDraft[i] ?? ''}
                    onChange={(v) =>
                      setRosterDraft(
                        Array.from({ length: slots }, (_, j) => (j === i ? v : rosterDraft[j] ?? metrics[j]?.id))
                      )
                    }
                    options={metrics.map((m) => ({ value: m.id, label: m.name }))}
                  />
                </AdminFormField>
              </View>
            ))}
          </View>
          {rosterDup ? <Text style={styles.error}>The same metric cannot be picked twice.</Text> : null}
          <View style={styles.footer}>
            <AdminButton
              label="Reset Changes"
              variant="secondary"
              disabled={!rosterDirty}
              onPress={() => setRosterDraft(roster)}
            />
            <AdminButton
              label={savingRoster ? 'Saving…' : 'Save Configuration'}
              disabled={!rosterDirty || !rosterFull || rosterDup || status === 'loading' || savingRoster}
              onPress={saveRoster}
            />
          </View>
        </View>
      </ConfigCard>

      {drawer?.kind === 'set' ? (
        <MetricSetDrawer
          key={`set:${key}`}
          org={org}
          dept={dept}
          current={deptMetrics[key] ?? []}
          onClose={() => setDrawer(null)}
          onSave={saveMetricSet}
        />
      ) : null}

      {drawer?.kind === 'national' || drawer?.kind === 'location' ? (
        <MetricTargetsDrawer
          key={`${drawer.kind}:${key}:${drawer.kind === 'location' ? drawer.loc ?? 'new' : ''}`}
          mode={drawer.kind}
          org={org}
          dept={dept}
          startLoc={drawer.kind === 'location' ? drawer.loc : undefined}
          locations={scopeLocations}
          metrics={metrics}
          targets={targets}
          today={today}
          next={next}
          history={ctx.history}
          onClose={() => setDrawer(null)}
          onSave={saveTargets}
        />
      ) : null}
    </TabBody>
  );
};

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  strong: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  sub: { fontSize: 12, fontFamily: FontFamily.regular, color: AdminColors.textMuted },
  // Numeric cells are right aligned; textAlign keeps stacked lines (value, "National", pending) flush right.
  own: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary, textAlign: 'right' },
  muted: { fontSize: 14, fontFamily: FontFamily.regular, color: AdminColors.textMuted, textAlign: 'right' },
  inheritTag: { fontSize: 10, fontFamily: FontFamily.regular, color: AdminColors.gray300, textAlign: 'right' },
  pending: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: FontFamily.bold,
    color: '#814705',
    marginTop: 2,
    textAlign: 'right',
  },
  rosterBody: { gap: 12 },
  rosterRow: { flexDirection: 'row', gap: 24 },
  rosterCol: { flex: 1 },
  error: { fontSize: 12, fontFamily: FontFamily.medium, color: AdminColors.textError },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginHorizontal: -24,
    marginBottom: -24,
    marginTop: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderTopColor: '#C6DCFB',
    backgroundColor: '#F8F8F8',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
});

export default MetricsTab;
